import { type FC, useEffect } from 'react';
import { useShallow } from 'zustand/react/shallow';
import NavigateToPartnerCta from '@/components/NavigateToPartnerCta';
import { FALLBACK_INDUSTRY } from '@/constants';
import { useHostContext } from '@/context/HostContext.tsx';
import StepsContainer from '@/pages/AgentTraining/components/StepsContainer';
import StepsLoader from '@/pages/AgentTraining/components/StepsLoader';
import { agentTrainingRealtime } from '@/services/AgentTrainingRealtime';
import { useAgentTraining } from '@/store/agent-training';
import useAppStore from '@/store/app.ts';
import { TScrapingStatus } from '@/types/agent-training.ts';
import { cn } from '@/utils';

const AgentTraining: FC = () => {
	const { setCustomerData, setAgentData } = useAppStore(
		useShallow((state) => ({
			externalId: state.externalId,
			setAgentData: state.setAgentData,
			setCustomerData: state.setCustomerData
		}))
	);

	const { isMiniCreatorMode, isOverlayMode } = useHostContext();

	const { setScrapingStatus } = useAgentTraining();

	useEffect(() => {
		const offCommand = agentTrainingRealtime.onNotifyScrapingFinished(
			(payload) => {
				setAgentData({
					agent_name: payload.agent_name,
					agent_title: payload.agent_title,
					business_name: payload.business_name,
					user_actor_id: payload.user_actor_id
				});

				setCustomerData({
					industry: payload.industry || FALLBACK_INDUSTRY,
					country_code: payload.country_code
				});

				setScrapingStatus(TScrapingStatus.FINISHED);
			}
		);
		return () => {
			offCommand();
		};
	}, [setScrapingStatus, setAgentData, setCustomerData]);

	useEffect(() => {
		globalThis.scrollTo(0, 0);
	}, []);

	return (
		<div
			className={cn('flex flex-col-reverse relative w-full min-h-[80vh]', {
				'md:grid md:grid-cols-[1fr_1fr] xl:grid-cols-[1fr_1fr] sm:grid-rows-[1fr] xs:max-h-screen gap-6':
					!isMiniCreatorMode,
				'gap-2 overflow-hidden text-sm h-full !min-h-[auto]': isMiniCreatorMode,
				'!h-[min-content] !min-h-[auto]': isOverlayMode
			})}
		>
			<StepsLoader />

			<StepsContainer />

			{isMiniCreatorMode && (
				<NavigateToPartnerCta className="w-full text-xs rounded-md border-primary-900 bg-white text-primary-900" />
			)}
		</div>
	);
};

export default AgentTraining;
